import { Type, type Static } from '@sinclair/typebox';
import { IsoDate, IsoDateTime } from './common';
import { createSchema } from '../../util/schema';

export type FeatureFlagMeta = {
  owner: string;
  expectedRemovalDate: string;
  description?: string;
};

/**
 * Optional committed catalog. Keys listed here always appear on the Admin page (default off)
 * even before the first save. Owner / removal date for registered keys come from here.
 * You can also create flags from the Admin UI without adding them here first.
 *
 * Missing Dynamo rows and unknown keys always evaluate false on the client (`isEnabled`).
 */
export const FLAG_REGISTRY: Readonly<Record<string, FeatureFlagMeta>> = {
  // intentionally empty — platform only
};

export function listFlagKeys(): string[] {
  return Object.keys(FLAG_REGISTRY);
}

export function isRegisteredFlagKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(FLAG_REGISTRY, key);
}

/** Override a flag on/off for one datasource (beats global). */
export const FeatureFlagDatasourceOverride = Type.Object(
  {
    datasource: Type.String({ minLength: 1 }),
    enabled: Type.Boolean(),
  },
  { $id: 'FeatureFlagDatasourceOverride' },
);
export type FeatureFlagDatasourceOverride = Static<typeof FeatureFlagDatasourceOverride>;
export const featureFlagDatasourceOverrideSchema = createSchema(FeatureFlagDatasourceOverride);

/** Override a flag on/off for one email (beats datasource and global). */
export const FeatureFlagEmailOverride = Type.Object(
  {
    email: Type.String({ format: 'email' }),
    enabled: Type.Boolean(),
  },
  { $id: 'FeatureFlagEmailOverride' },
);
export type FeatureFlagEmailOverride = Static<typeof FeatureFlagEmailOverride>;
export const featureFlagEmailOverrideSchema = createSchema(FeatureFlagEmailOverride);

/** Evaluated booleans for the authenticated caller (no metadata / targeting lists). */
export const FeatureFlagsEval = Type.Object(
  {
    flags: Type.Record(Type.String(), Type.Boolean()),
  },
  { $id: 'FeatureFlagsEval' },
);
export type FeatureFlagsEval = Static<typeof FeatureFlagsEval>;
export const featureFlagsEvalSchema = createSchema(FeatureFlagsEval);

/** One flag as shown on the Admin Feature Flags page. */
export const FeatureFlagAdminItem = Type.Object(
  {
    key: Type.String({ minLength: 1 }),
    /** Global default for everyone. */
    enabled: Type.Boolean(),
    datasourceOverrides: Type.Array(FeatureFlagDatasourceOverride),
    emailOverrides: Type.Array(FeatureFlagEmailOverride),
    owner: Type.Optional(Type.String({ minLength: 1 })),
    expectedRemovalDate: Type.Optional(IsoDate),
    description: Type.Optional(Type.String()),
    stale: Type.Boolean(),
    updatedAt: Type.Optional(IsoDateTime),
    updatedBy: Type.Optional(Type.String()),
    version: Type.Integer({ minimum: 0 }),
  },
  { $id: 'FeatureFlagAdminItem' },
);
export type FeatureFlagAdminItem = Static<typeof FeatureFlagAdminItem>;
export const featureFlagAdminItemSchema = createSchema(FeatureFlagAdminItem);

export const FeatureFlagAdminList = Type.Object(
  {
    flags: Type.Array(FeatureFlagAdminItem),
  },
  { $id: 'FeatureFlagAdminList' },
);
export type FeatureFlagAdminList = Static<typeof FeatureFlagAdminList>;
export const featureFlagAdminListSchema = createSchema(FeatureFlagAdminList);

export const UpsertFeatureFlag = Type.Object(
  {
    enabled: Type.Boolean(),
    datasourceOverrides: Type.Optional(Type.Array(FeatureFlagDatasourceOverride)),
    emailOverrides: Type.Optional(Type.Array(FeatureFlagEmailOverride)),
    /** Optimistic concurrency — omit on first write for a key; required thereafter. */
    version: Type.Optional(Type.Integer({ minimum: 0 })),
    /**
     * Required when creating a key that is not already in FLAG_REGISTRY.
     * Ignored for registered keys (registry remains source of truth for owner/date).
     */
    owner: Type.Optional(Type.String({ minLength: 1 })),
    expectedRemovalDate: Type.Optional(IsoDate),
    description: Type.Optional(Type.String()),
  },
  { $id: 'UpsertFeatureFlag' },
);
export type UpsertFeatureFlag = Static<typeof UpsertFeatureFlag>;
export const upsertFeatureFlagSchema = createSchema(UpsertFeatureFlag);

/** Query param for mutate-by-key routes (supports `/` in keys via encodeURIComponent). */
export const FlagKeyQuery = Type.Object(
  {
    key: Type.String({ minLength: 1, description: 'Feature flag key (may include / - _ .)' }),
  },
  { $id: 'FlagKeyQuery' },
);
export type FlagKeyQuery = Static<typeof FlagKeyQuery>;
export const flagKeyQuerySchema = createSchema(FlagKeyQuery);

/** Lowercase keys; `.` `-` `_` `/` separate segments, e.g. `tearsheets/new-builder`. */
export function isValidFlagKey(key: string): boolean {
  return /^[a-z][a-z0-9]*(?:[._/-][a-z0-9]+)*$/.test(key.trim());
}
